import React, { useMemo, useState } from "react";
import Modal from "../components/Modal";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Loader,
  PageHeader,
  Select,
  Textarea,
} from "../components/ui";
import {
  useApproveClaim,
  useClaims,
  useRejectClaim,
} from "../apiCalls/claimApiCalls";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "—";

// Two dates that agree are the cheapest signal an admin has, so say plainly
// whether they match rather than making them compare two strings.
const DobComparison = ({ claimed, stored }) => {
  if (!claimed || !stored) {
    return (
      <span className="text-ink-soft">
        {formatDate(claimed)} vs {formatDate(stored)}
      </span>
    );
  }

  const matches =
    new Date(claimed).toDateString() === new Date(stored).toDateString();

  return (
    <span className={matches ? "text-teal-dark" : "text-danger"}>
      {formatDate(claimed)} vs {formatDate(stored)}{" "}
      {matches ? "· match" : "· mismatch"}
    </span>
  );
};

export default function Claims() {
  const [status, setStatus] = useState("pending");
  // { claim, rivalCount } — the approval waiting on confirmation.
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  // Which row is being decided. The mutation's own isLoading is global to the
  // hook, so on its own it greys out every row on the page and an admin can't
  // tell which one is actually working.
  const [decidingId, setDecidingId] = useState(null);

  const { data: claims, isLoading, isError, refetch } = useClaims(status);
  const { mutate: approve, isLoading: isApproving } = useApproveClaim();
  const { mutate: reject, isLoading: isRejecting } = useRejectClaim();

  const isDeciding = isApproving || isRejecting;

  // Group by target player: contention is the case that needs judgement, so
  // the layout should make it impossible to miss.
  const groups = useMemo(() => {
    const byPlayer = new Map();

    (claims || []).forEach((claim) => {
      const key = claim.player?._id ?? "unknown";
      if (!byPlayer.has(key)) {
        byPlayer.set(key, { player: claim.player, claims: [] });
      }
      byPlayer.get(key).claims.push(claim);
    });

    return [...byPlayer.values()];
  }, [claims]);

  const confirmApproval = () => {
    const { claim } = approving;
    setDecidingId(claim._id);
    approve(claim._id, {
      onSuccess: () => setApproving(null),
      onSettled: () => setDecidingId(null),
    });
  };

  const submitRejection = () => {
    setDecidingId(rejecting._id);
    reject(
      { id: rejecting._id, reason: reason.trim() },
      {
        onSuccess: () => {
          setRejecting(null);
          setReason("");
        },
        onSettled: () => setDecidingId(null),
      }
    );
  };

  return (
    <>
      <PageHeader
        title="Claims"
        subtitle="People asking to take ownership of a profile already in the register."
        actions={
          <Select
            aria-label="Filter claims by status"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="w-40"
          >
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        }
      />

      {isLoading && <Loader label="Loading claims" />}
      {isError && (
        <ErrorState message="Couldn't load the claim queue." onRetry={refetch} />
      )}

      {claims && claims.length === 0 && (
        <EmptyState message={`No ${status} claims.`} />
      )}

      <div className="flex flex-col gap-6">
        {groups.map(({ player, claims: group }) => (
          <Card key={player?._id ?? "unknown"} className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg font-bold uppercase tracking-tight">
                  {player?.name ?? "Unknown player"}
                </h2>
                <p className="text-sm text-ink-soft">
                  {player?.club?.name || "No club"} · {player?.status}
                </p>
              </div>
              {group.length > 1 && status === "pending" && (
                <Badge tone="pending">
                  Contested — {group.length} claims
                </Badge>
              )}
            </div>

            <div className="mt-4 flex flex-col gap-4">
              {group.map((claim) => (
                <div
                  key={claim._id}
                  className="border-t border-sand pt-4 first:border-0 first:pt-0"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 text-sm">
                      <div className="font-medium">{claim.accountEmail}</div>
                      <div className="mt-1 text-ink-soft">
                        Date of birth:{" "}
                        <DobComparison
                          claimed={claim.claimedDob}
                          stored={claim.playerDateOfBirth}
                        />
                      </div>
                      <div className="mt-1 text-ink-soft">
                        Phone: {claim.phone || "—"}
                      </div>
                      {claim.note && (
                        <p className="mt-2 max-w-prose text-ink">
                          "{claim.note}"
                        </p>
                      )}
                      <div className="mt-2">
                        {claim.documentUrl ? (
                          <a
                            href={claim.documentUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-teal-dark underline"
                          >
                            View photo ID
                          </a>
                        ) : (
                          <span className="text-ink-soft">
                            No photo ID attached
                          </span>
                        )}
                      </div>
                      {claim.decisionReason && (
                        <p className="mt-2 text-ink-soft">
                          Reason: {claim.decisionReason}
                        </p>
                      )}
                    </div>

                    {claim.status === "pending" && (
                      <div className="flex shrink-0 gap-2">
                        <Button
                          variant="primary"
                          disabled={isDeciding}
                          onClick={() =>
                            setApproving({
                              claim,
                              rivalCount: group.length - 1,
                            })
                          }
                        >
                          {decidingId === claim._id && isApproving
                            ? "Approving…"
                            : "Approve"}
                        </Button>
                        <Button
                          variant="danger"
                          disabled={isDeciding}
                          onClick={() => setRejecting(claim)}
                        >
                          {decidingId === claim._id && isRejecting
                            ? "Rejecting…"
                            : "Reject"}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {approving && (
        // Same shape as ConfirmDelete: state what happens and what it takes
        // with it. A browser confirm() couldn't carry the rival warning
        // legibly, and one tick of Chrome's "block additional dialogs" made
        // the approve button do nothing at all.
        <Modal
          title="Approve claim"
          onClose={() => setApproving(null)}
          footer={
            <>
              <Button onClick={() => setApproving(null)}>Cancel</Button>
              <Button
                variant="primary"
                disabled={isApproving}
                onClick={confirmApproval}
              >
                {isApproving ? "Approving…" : "Approve claim"}
              </Button>
            </>
          }
        >
          <p className="text-sm text-ink-soft">
            This links{" "}
            <strong className="text-ink">{approving.claim.accountEmail}</strong>{" "}
            to{" "}
            <strong className="text-ink">
              {approving.claim.player?.name ?? "this player"}
            </strong>
            . They will be able to edit that profile.
          </p>

          {approving.rivalCount > 0 && (
            // The irreversible half of the decision — an admin must not be
            // able to miss it.
            <p className="mt-4 border-l-4 border-danger bg-danger-wash px-4 py-3 text-sm font-medium text-danger">
              {approving.rivalCount} other pending claim
              {approving.rivalCount === 1 ? "" : "s"} on this profile will be
              rejected automatically, and cannot be reopened.
            </p>
          )}
        </Modal>
      )}

      {rejecting && (
        <Modal
          title={`Reject ${rejecting.accountEmail}'s claim`}
          onClose={() => setRejecting(null)}
          footer={
            <>
              <Button onClick={() => setRejecting(null)}>Cancel</Button>
              <Button
                variant="danger"
                disabled={!reason.trim() || isRejecting}
                onClick={submitRejection}
              >
                Reject claim
              </Button>
            </>
          }
        >
          <Field
            label="Reason"
            hint="The claimant sees this, so make it something they can act on."
          >
            <Textarea
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. The date of birth doesn't match our records."
            />
          </Field>
        </Modal>
      )}
    </>
  );
}
